import { Box, IconButton, Stack } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Field, useFormikContext } from 'formik';

import { sxConfig } from './joker.style';
import { AddButtonUi, AutoCompleteSelectUI, ConfirmDialogUI, FormErrorUI, FormLabelUI } from '../Interface';
import newDocumentTypeService from '../../service/newDocumentType.service';
import { JokerUngroupedOption, JokerTypeList, JokerFieldsCountByType, JokerTypes } from '../../config/joker';
import jokerService from '../../service/joker.service';
import { generateAutocompleteItems } from '../../utils/helper';
import EditIcon from '@mui/icons-material/Edit';
import JokerGroupDialog from './JokerGroupDialog';
import JokerFieldsTable from './JokerFieldsTable';
import { normalizeJokerFields } from '../../utils/jokerHelper';
import JokerSelectListDialog from './JokerSelectListDialog';
import { getDocumentTypeKeyUpperCase } from '../../utils/DocHelper';
import useMenus from '../../context/MenuContext';

export const JokerForm = ({ valuesToForm, isDirty, setIsInitialized }) => {
  const { menus, getSubMenuNamesByUrls } = useMenus();
  const { values, setFieldValue, resetForm, errors, validateField } = useFormikContext();
  const defGroupDialogData = { id: null, open: false, groupName: '' };
  const defSelectListDialogData = { id: null, open: false, valueIndex: null, originalValues: [] };
  const defSelectListVal = { value: '', label: '', visOrder: null };
  const defConfirmDialogData = { open: false, message: '', handleChangeElement: () => {}, onHide: () => {} };

  const [groupDialogData, setGroupDialogData] = useState(defGroupDialogData);
  const [selectListDialogData, setSelectListDialogData] = useState(defSelectListDialogData);
  const [confirmDialogData, setConfirmDialogData] = useState(defConfirmDialogData);
  const [autoCompleteOptions, setAutoCompleteOptions] = useState({
    jokerType: [],
    jokerGroup: [],
  });

  const submenuNames = useMemo(() => {
    return getSubMenuNamesByUrls(['resourceOne', 'resourceTwo', 'resourceThree', 'resourceFour', 'resourceFive']);
  }, [menus]);

  useEffect(() => {
    getJokerGroups();
  }, [values.type]);

  useEffect(() => {
    getEditorFields();
  }, [values.groupId]);

  useEffect(() => {
    generateAutoCompleteData();
  }, [submenuNames]);

  const generateAutoCompleteData = () => {
    newDocumentTypeService.allDocumentTypes().then((data) => {
      const formattedDoctypes = data.map((docType) => {
        return {
          label: `B - ${docType.name}`,
          value: getDocumentTypeKeyUpperCase(docType.documentTypeId),
        };
      });

      const jokerTypes = JokerTypeList.map((item) => {
        switch (item.value) {
          case JokerTypes.RESOURCEONE:
            return {
              ...item,
              label: submenuNames[0] ?? item.label,
            };

          case JokerTypes.RESOURCETWO:
            return {
              ...item,
              label: submenuNames[1] ?? item.label,
            };

          case JokerTypes.RESOURCETHREE:
            return {
              ...item,
              label: submenuNames[2] ?? item.label,
            };

          case JokerTypes.RESOURCEFOUR:
            return {
              ...item,
              label: submenuNames[3] ?? item.label,
            };

          case JokerTypes.RESOURCEFIVE:
            return {
              ...item,
              label: submenuNames[4] ?? item.label,
            };

          default:
            return item;
        }
      });

      setAutoCompleteOptions((prev) => ({
        ...prev,
        jokerType: [...jokerTypes, ...formattedDoctypes],
      }));
    });
  };

  const getEditorFields = () => {
    if (!values.groupId) {
      setFieldValue('fields', []);
      setIsInitialized(false);
      return;
    }

    jokerService.getEditorFields(values.type, values.groupId).then((data) => {
      const normalized = normalizeJokerFields(data, values.type, values.groupId === -1 ? true : false);
      valuesToForm(normalized, values.type, values.groupId);
    });
  };

  const getJokerGroups = () => {
    if (!values.type) {
      setAutoCompleteOptions((prev) => ({ ...prev, jokerGroup: [] }));
      return;
    }
    jokerService.getGroups(values.type).then((data) => {
      const groups = generateAutocompleteItems(data, 'name', 'id');

      const hasRowLevelFields = !!JokerFieldsCountByType[values.type]?.ROWS;

      setAutoCompleteOptions((prev) => ({
        ...prev,
        jokerGroup: hasRowLevelFields ? [...groups, JokerUngroupedOption] : groups,
      }));
    });
  };

  const handleUpsertGroup = (name) => {
    const body = {
      name,
      type: values.type,
      id: values.groupId,
    };

    jokerService.upsertGroup(body).then((data) => {
      if (values.groupId) {
        setAutoCompleteOptions((prev) => ({
          ...prev,
          jokerGroup: prev.jokerGroup.map((g) => (g.value === data.id ? { ...g, label: data.name } : g)),
        }));
      } else {
        setAutoCompleteOptions((prev) => ({
          ...prev,
          jokerGroup: [...prev.jokerGroup, { label: data.name, value: data.id }],
        }));

        setFieldValue('groupId', data.id);
      }

      setGroupDialogData(defGroupDialogData);
    });
  };

  const handleActionWithGuard = (action) => {
    if (isDirty) {
      setConfirmDialogData({
        open: true,
        message: 'El nem mentett változásai vannak! Biztosan folytatja?',
        handleChangeElement: () => {
          action();
          setConfirmDialogData(defConfirmDialogData);
        },
        onHide: () => {
          setFieldValue('groupId', values.groupId);
          setConfirmDialogData(defConfirmDialogData);
        },
      });
    } else {
      action();
    }
  };

  return (
    <>
      <Stack direction="row" spacing={2} sx={sxConfig.fieldContainer}>
        <Box sx={sxConfig.fieldBox}>
          <Field
            type="autocomplete"
            component={AutoCompleteSelectUI}
            helperText={<FormErrorUI message={errors.type} />}
            name="type"
            label={<FormLabelUI text="Felületek" />}
            dataset={autoCompleteOptions.jokerType}
            onChange={(_e, newVal) => {
              setFieldValue('type', newVal?.value ?? null).then(() => {
                validateField('type');
              });
              setFieldValue('groupId', null).then(() => {
                validateField('groupId');
              });
            }}
            selectedValue={values.type}
          />
        </Box>
        <Box sx={sxConfig.fieldBox}>
          <Stack direction="row" spacing={1}>
            <Field
              type="autocomplete"
              component={AutoCompleteSelectUI}
              helperText={<FormErrorUI message={errors.groupId} />}
              name="groupId"
              label={<FormLabelUI text="Csoportok" />}
              dataset={autoCompleteOptions.jokerGroup}
              onChange={(_e, newVal) => {
                handleActionWithGuard(() => {
                  setFieldValue('groupId', newVal?.value ?? null).then(() => {
                    validateField('groupId');
                  });
                });
              }}
              selectedValue={values.groupId}
              isOptionEqualToValue={(option, value) => option.value === value}
              disabled={!values.type}
            />
            <Stack direction="row" spacing={1} sx={sxConfig.fieldAddBtn}>
              <AddButtonUi
                disabled={!values.type}
                onClick={() => {
                  handleActionWithGuard(() => {
                    setFieldValue('groupId', null);
                    setGroupDialogData({ open: true });
                  });
                }}
              />
              <IconButton
                sx={sxConfig.editIcon}
                disabled={!values.groupId || values.groupId === -1}
                onClick={() => {
                  const finded = autoCompleteOptions.jokerGroup.find((group) => group.value === values.groupId)?.label;

                  setGroupDialogData({
                    open: true,
                    id: values.groupId,
                    groupName: finded,
                  });
                }}
              >
                <EditIcon fontSize={values.groupId ? 'medium' : 'small'} />
              </IconButton>
            </Stack>
          </Stack>
        </Box>
      </Stack>
      <JokerFieldsTable setSelectListDialogData={setSelectListDialogData} />
      <JokerSelectListDialog
        dialogData={selectListDialogData}
        handleClose={() => setSelectListDialogData(defSelectListDialogData)}
        defaultField={defSelectListVal}
      />
      <JokerGroupDialog
        dialogData={groupDialogData}
        handleClose={() => setGroupDialogData(defGroupDialogData)}
        onSave={handleUpsertGroup}
      />
      <ConfirmDialogUI
        handleChangeElement={confirmDialogData.handleChangeElement}
        show={confirmDialogData.open}
        onHide={confirmDialogData.onHide}
        message={confirmDialogData.message}
      />
    </>
  );
};
